import { forwardRef } from 'react';
import type { CSSProperties, KeyboardEvent, MouseEvent, ReactNode } from 'react';
import { SiteLink as Link } from './SiteLink';
import { scrollToId } from '../lib/scroll';

/**
 * The one interactive primitive, the way Ledger is the one content primitive.
 *
 * Before this existed, every control on the site was a Tamagui `Text`  a
 * `<span>`  with an `onPress` handler. Measured on the landing page: zero
 * `<button>` elements, five `<a>` elements of which all five were external
 * footer links, and 28 distinct controls that were neither native focusables
 * nor had a focusable ancestor. The entire signup path was unreachable by
 * keyboard, uncopyable by right-click, and invisible to a crawler.
 *
 * Which element gets rendered is decided by what the control actually does,
 * not by how it looks:
 *
 *   to=       an in-app route            -> react-router <Link>
 *   href=     off-site, mailto, or /#id  -> <a>, with rel on new tabs
 *   onPress=  changes state on this page -> <button type="button">
 *
 * `to` and `href` are exclusive and one of the three is required. A control
 * that navigates must be a link, because a link is the only thing the browser
 * gives cmd-click, middle-click, "copy link address" and a crawlable href for
 * free.
 *
 * TONES are the four button shapes that actually exist on this site, measured
 * off the rendered page rather than invented:
 *
 *   primary    gold fill, the signup action                (4 instances)
 *   secondary  hairline outline                            (1 instance)
 *   surface    $color4 fill, the Enterprise tier action     (1 instance)
 *   bare       no styling at all
 *
 * `bare` is the important one. It resets the element to inherit everything and
 * takes its appearance entirely from its children, so an existing Tamagui
 * `<Text>` can be wrapped in one and gain a real anchor, a real focus ring and
 * a real keyboard handler with no visual change whatsoever. Every nav item,
 * footer link and FAQ trigger uses it.
 *
 * APPEARANCE LIVES IN CSS, not here. `.act` plus `.act-{tone}` plus
 * `.act-{size}` in global.css carry the reset, the metrics and the tones, which
 * is what makes `:hover`, `:active` and `:disabled` expressible at all, an
 * inline style attribute cannot hold a pseudo-class, and that is the whole
 * reason no control on this site had a hover state.
 *
 * The rule this component must never break: the rendered `style` attribute
 * contains the CALLER'S `style` prop and nothing else. Inline is (1,0,0,0). If
 * the reset were still written inline, `background: none`, `color: inherit`,
 * `border: 0` and `padding: 0` would each beat the class that is trying to set
 * them, and every control on 23 routes would render as unstyled inline text, * while the elements, hrefs, roles and tab order all still checked out.
 *
 * Radius reads `var(--radius-sm)`, and that name is load-bearing. It used to be
 * `var(--borderRadiusSm)`, which Tamagui also emits, as the unitless number `6`,
 * at `:root.t_dark` (0,2,0), outranking global.css's `6px` at `:root` (0,1,0).
 * `border-radius: 6` is invalid, so the primary action on every page rendered
 * square while four pricing blocks carried 8px corners.
 */

type Tone = 'primary' | 'secondary' | 'surface' | 'bare';
type Size = 'sm' | 'md' | 'lg';

export interface ActionProps {
  children: ReactNode;
  /** In-app route. Renders a react-router Link. */
  to?: string;
  /** Off-site URL, mailto:, or an /#anchor. Renders an anchor. */
  href?: string;
  /** Opens in a new tab. Only meaningful with href. */
  external?: boolean;
  /**
   * Changes state on this page. Renders a button.
   *
   * Allowed alongside `to` as well, where it runs in addition to the
   * navigation: the logo is a link to `/` that must also scroll to the top, and
   * a same-route click changes no pathname, so nothing else would fire.
   */
  onPress?: () => void;
  tone?: Tone;
  size?: Size;
  /** Passed through for disclosure controls. */
  'aria-expanded'?: boolean;
  'aria-controls'?: string;
  'aria-label'?: string;
  'aria-haspopup'?: boolean | 'menu' | 'true';
  id?: string;
  onFocus?: () => void;
  onBlur?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  onKeyDown?: (e: KeyboardEvent) => void;
  style?: CSSProperties;
  className?: string;
  /** Stretch to the width of its container, for tier cards. */
  block?: boolean;
}

export const Action = forwardRef<HTMLElement, ActionProps>(function Action(
  { children, to, href, external, onPress, tone = 'bare', size = 'md', style, block, className, ...rest },
  ref,
) {
  // `className` has to be destructured explicitly. It was declared on the props
  // interface but left to fall through `...rest`, which was harmless while
  // nothing internal set a class and would silently drop every tone now.
  const classes = ['act'];
  if (tone !== 'bare') {
    classes.push(`act-${tone}`, `act-${size}`);
    if (block) classes.push('act-block');
  }
  if (className) classes.push(className);

  const shared = {
    ...rest,
    className: classes.join(' '),
    // The caller's style and nothing else, see the header comment.
    style,
  };

  if (to !== undefined) {
    return (
      <Link ref={ref as never} to={to} onClick={onPress} {...shared}>
        {children}
      </Link>
    );
  }

  if (href !== undefined) {
    // An /#anchor on the current page: let the browser own the href so the link
    // is real, but scroll it ourselves so the motion preference is respected.
    const hash = href.startsWith('/#') ? href.slice(2) : null;
    const onClick = (e: MouseEvent) => {
      onPress?.();
      if (!hash) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
      // Only intercept when the target is on this page. From a subpage, let the
      // browser navigate to `/` and land on the fragment itself, the
      // alternative is navigate('/') plus a setTimeout guessing at when the
      // node exists, which is what both nav components used to do.
      if (window.location.pathname !== '/') return;
      e.preventDefault();
      scrollToId(hash);
    };

    return (
      <a
        ref={ref as never}
        href={href}
        onClick={onClick}
        target={external ? '_blank' : undefined}
        // window.open(url, '_blank') does NOT imply noopener the way an anchor
        // does, and every CTA used to go through window.open. So the signup
        // page was handed a live window.opener back into statio.dev.
        rel={external ? 'noopener noreferrer' : undefined}
        {...shared}
      >
        {children}
      </a>
    );
  }

  return (
    <button ref={ref as never} type="button" onClick={onPress} {...shared}>
      {children}
    </button>
  );
});
